import React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { DEFAULT_TAG_COLOR } from "@/components/Shared/TagChip/TagChip";
import { contrastTextColor } from "@/utils/color.utils";

export interface TagPickerOption {
	key: string;
	name: { sv: string };
	/** Optional display color (hex #RRGGBB) from the tag type. */
	color?: string;
}

interface TagPickerProps {
	/** The taxonomy to pick from (e.g. org `customerTagTypes` / `jobTagTypes`). */
	options: TagPickerOption[];
	/** Selected tag keys. */
	value: string[];
	onChange: (keys: string[]) => void;
	/** Placeholder of the add-select, e.g. "Lägg till tagg…". */
	placeholder?: string;
	/** Shown when nothing is selected. */
	emptyText?: string;
	disabled?: boolean;
	className?: string;
}

/**
 * A simple key-based multi-select over an org tag taxonomy: selected tags as
 * removable pills + a select to add more. Entities store only `{ key }`, so
 * display names are resolved from `options` (falling back to the raw key for
 * keys deleted from the taxonomy).
 */
const TagPicker: React.FC<TagPickerProps> = ({
	options,
	value,
	onChange,
	placeholder = "Lägg till tagg…",
	emptyText,
	disabled,
	className,
}) => {
	const nameOf = (key: string) => options.find((o) => o.key === key)?.name?.sv ?? key;
	const remaining = options.filter((o) => !value.includes(o.key));

	const add = (key: string) => {
		if (key && !value.includes(key)) onChange([...value, key]);
	};

	const remove = (key: string) => onChange(value.filter((k) => k !== key));

	return (
		<div className={cn("flex flex-col gap-2", className)}>
			{value.length === 0 && emptyText && <p className="text-sm text-gray-500">{emptyText}</p>}
			{value.length > 0 && (
				<div className="flex flex-wrap items-center gap-1.5">
					{value.map((key) => {
						// Trello-style chip: the tag type's color as background with
						// contrast-picked text, matching TagChip/list views.
						const background = options.find((o) => o.key === key)?.color || DEFAULT_TAG_COLOR;
						const textColor = contrastTextColor(background);
						return (
							<span
								key={key}
								className="inline-flex items-center gap-1 rounded py-1 pl-3 pr-1.5 text-sm font-medium"
								style={{ backgroundColor: background, color: textColor }}
							>
								{nameOf(key)}
								<button
									type="button"
									onClick={() => remove(key)}
									disabled={disabled}
									aria-label={`Ta bort ${nameOf(key)}`}
									title="Ta bort"
									className="flex h-5 w-5 items-center justify-center rounded border-0 bg-transparent text-current opacity-70 hover:bg-black/10 hover:opacity-100"
								>
									<X size={13} />
								</button>
							</span>
						);
					})}
				</div>
			)}

			{remaining.length > 0 && (
				<div className="flex flex-wrap items-center gap-2">
					<select
						value=""
						disabled={disabled}
						onChange={(e) => add(e.target.value)}
						className="rounded-md border border-solid border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800"
					>
						<option value="">{placeholder}</option>
						{remaining.map((o) => (
							<option key={o.key} value={o.key}>
								{o.name.sv}
							</option>
						))}
					</select>
				</div>
			)}
		</div>
	);
};

export default TagPicker;
